import { ReactComponent as DeleteSvg } from '@trinsly/common/src/assets/icons/delete.svg'
import Clickable from '@trinsly/common/src/components/atoms/clickable'
import Column from '@trinsly/common/src/components/atoms/column'
import Row from '@trinsly/common/src/components/atoms/row'
import Separator from '@trinsly/common/src/components/atoms/separator'
import Spinner from '@trinsly/common/src/components/atoms/spinner'
import Table from '@trinsly/common/src/components/atoms/table'
import Text from '@trinsly/common/src/components/atoms/text'
import IconButton from '@trinsly/common/src/components/molecules/icon-button'
import NonIdealState from '@trinsly/common/src/components/organisms/non-ideal-state'
import useDeleteProspectEmployment from 'api-hooks/use-delete-prospect-employment'
import useGetProspectEmployments from 'api-hooks/use-get-prospect-employments'
import { format, parseISO } from 'date-fns'
import React from 'react'
import { toast } from 'react-toastify'
import ProspectType from 'types/prospect-type'
import EmploymentFormModal from './employment-form-modal'

type EmploymentsSectionProps = {
  prospectId: ProspectType['id'] | undefined
}

function EmploymentsSection({ prospectId }: EmploymentsSectionProps) {
  const [showFormModal, setShowFormModal] = React.useState(false)
  const [pendingDeleteMap, setPendingDeleteMap] = React.useState<{
    [index: string]: boolean
  }>({})
  const { data: employments, pending: employmentHistoryPending } =
    useGetProspectEmployments({ prospectId })

  const { fetch: deleteEmployment } = useDeleteProspectEmployment({
    onMutate: ({ employmentId }) => {
      setPendingDeleteMap((state) => ({
        ...state,
        [employmentId]: true,
      }))
    },
    onSuccess: (_, { employmentId }) => {
      setPendingDeleteMap((state) => ({
        ...state,
        [employmentId]: false,
      }))
      toast.success('Employment deleted!')
    },
    onError: (_, { employmentId }) => {
      setPendingDeleteMap((state) => ({
        ...state,
        [employmentId]: false,
      }))
      toast.error('Error deleting Employment')
    },
  })

  const pending = !prospectId || employmentHistoryPending
  const isNonIdealState = !pending && !employments?.length

  return (
    <Column className="px-md">
      <Row className="items-center justify-between gap-sm">
        <Text size="xs">Employment History</Text>
        <Clickable
          onClick={() => {
            setShowFormModal(true)
          }}
        >
          <Row className="items-center gap-xs">
            {pending && <Spinner size="xs" />}
            <Text size="xs" color="info-500" className="underline">
              + New Employment ({employments?.length || 0})
            </Text>
          </Row>
        </Clickable>
      </Row>
      <Separator size="xs" />
      <Column className="border rounded overflow-hidden">
        {isNonIdealState ? (
          <Column className="p-md">
            <NonIdealState message="No Employment Data Available" />
          </Column>
        ) : (
          <Table
            pending={pending}
            data={employments}
            key={employments?.length || 10}
            pageSize={employments?.length || 10}
            columns={[
              {
                width: 300,
                header: 'Company',
                cell: (item) => item?.companyName || '--',
              },
              {
                width: 300,
                header: 'Role',
                cell: (item) => item?.role || '--',
              },
              {
                width: 120,
                header: 'Start Date',
                cell: (item) =>
                  item?.startTime
                    ? format(parseISO(item!.startTime), 'MM/dd/yyyy')
                    : '--',
              },
              {
                width: 120,
                header: 'End Date',
                cell: (item) =>
                  item?.endTime
                    ? format(parseISO(item!.endTime), 'MM/dd/yyyy')
                    : '--',
              },
              {
                width: 100,
                cell: (item) => {
                  return (
                    <Row className="items-center justify-end">
                      <IconButton
                        size="sm"
                        variant="transparent"
                        intent="danger"
                        pending={pendingDeleteMap[item?.id || 0]}
                        onClick={() => {
                          deleteEmployment({ employmentId: item?.id || 0 })
                        }}
                      >
                        <DeleteSvg />
                      </IconButton>
                    </Row>
                  )
                },
              },
            ]}
          />
        )}
      </Column>
      <EmploymentFormModal
        prospectId={prospectId}
        visible={showFormModal}
        onRequestClose={() => setShowFormModal(false)}
      />
    </Column>
  )
}

export default EmploymentsSection
